import { z } from 'zod';

export const SCAN_WINDOWS = ['1W', '1M', '3M', '6M', '1Y'] as const;
export const ScanWindowSchema = z.enum(SCAN_WINDOWS);
export type ScanWindow = z.infer<typeof ScanWindowSchema>;

export const CoilSetupSchema = z.enum(['3D', '5D', '3D + 5D']);
export type CoilSetup = z.infer<typeof CoilSetupSchema>;

export const CoilMetricsSchema = z.object({
  isTight: z.boolean(),
  setup: CoilSetupSchema.nullable(),
  closeAboveEma9: z.boolean(),
  closeSpread3dPct: z.number(),
  closeSpread5dPct: z.number(),
  coilRange5dAtr: z.number(),
  trueRangeRatio3d: z.number(),
  trueRangeRatio5d: z.number(),
  currentRangePctPriorAtr: z.number(),
  rmv15: z.number().nullable(),
});
export type CoilMetrics = z.infer<typeof CoilMetricsSchema>;

export const PairedEtfSchema = z.object({
  symbol: z.string(),
  name: z.string(),
  averageVolume30d: z.number(),
  adrPct: z.number(),
});
export type PairedEtf = z.infer<typeof PairedEtfSchema>;

export const ScanCandidateSchema = z.object({
  symbol: z.string(),
  name: z.string(),
  exchange: z.string(),
  industry: z.string(),
  close: z.number(),
  sma50: z.number(),
  adrPct: z.number(),
  atrPct: z.number(),
  averageVolume10d: z.number(),
  averageDollarVolume30d: z.number(),
  performance: z.object({
    '1W': z.number(),
    '1M': z.number(),
    '3M': z.number(),
    '6M': z.number(),
    '1Y': z.number(),
  }),
  topWindows: z.array(ScanWindowSchema),
  momentumScore: z.number(),
  atrExtension: z.number(),
  coil: CoilMetricsSchema.nullable(),
  pairedEtf: PairedEtfSchema.nullable(),
  /** The data vendor's industry when the user regrouped this stock; `industry` holds their group. */
  vendorIndustry: z.string().optional(),
});
export type ScanCandidate = z.infer<typeof ScanCandidateSchema>;

const IndustryCountSchema = z.object({ industry: z.string(), leaders: z.number() });
export const IndustryLeadershipSchema = z.object({
  '1W': z.array(IndustryCountSchema),
  '1M': z.array(IndustryCountSchema),
  '3M': z.array(IndustryCountSchema),
  '6M': z.array(IndustryCountSchema),
  '1Y': z.array(IndustryCountSchema),
});
export type IndustryLeadership = z.infer<typeof IndustryLeadershipSchema>;

export const StockLeadersSnapshotSchema = z.object({
  sessionDate: z.string(),
  generatedAt: z.string(),
  settings: z.object({
    minAverageDollarVolume30d: z.number(),
    minAdrPct: z.number(),
    minAverageVolume10d: z.number(),
    topPct: z.number(),
    maxAtrExtension: z.number(),
    /** Absent on scans before scan settings existed, which always excluded biotech. */
    excludeBiotech: z.boolean().optional(),
    superLiquidMinAverageDollarVolume30d: z.number(),
    superLiquidTopPerWindow: z.number(),
  }),
  counts: z.object({
    scanned: z.number(),
    eligible: z.number(),
    leaders: z.number(),
    nonExtended: z.number(),
    tight: z.number(),
    superLiquid: z.number(),
  }),
  nonExtendedLeaders: z.array(ScanCandidateSchema),
  superLiquid: z.array(ScanCandidateSchema),
  coilUnavailable: z.array(z.string()),
  /** Industries among all momentum leaders, extended or not; absent on scans before it existed. */
  industryLeadership: IndustryLeadershipSchema.optional(),
});
export type StockLeadersSnapshot = z.infer<typeof StockLeadersSnapshotSchema>;

/** Bounds for user scan thresholds; the stored universe and coil checks cover the loosest values. */
export const SCAN_SETTING_LIMITS = {
  minAverageDollarVolume30d: { min: 10_000_000, max: 1_000_000_000 },
  minAdrPct: { min: 3, max: 15 },
  minAverageVolume10d: { min: 200_000, max: 10_000_000 },
  topPct: { min: 0.01, max: 0.1 },
  maxAtrExtension: { min: 1, max: 6 },
} as const;

export const ScanSettingsSchema = z.object({
  minAverageDollarVolume30d: z
    .number()
    .min(SCAN_SETTING_LIMITS.minAverageDollarVolume30d.min)
    .max(SCAN_SETTING_LIMITS.minAverageDollarVolume30d.max),
  minAdrPct: z
    .number()
    .min(SCAN_SETTING_LIMITS.minAdrPct.min)
    .max(SCAN_SETTING_LIMITS.minAdrPct.max),
  minAverageVolume10d: z
    .number()
    .int()
    .min(SCAN_SETTING_LIMITS.minAverageVolume10d.min)
    .max(SCAN_SETTING_LIMITS.minAverageVolume10d.max),
  topPct: z.number().min(SCAN_SETTING_LIMITS.topPct.min).max(SCAN_SETTING_LIMITS.topPct.max),
  maxAtrExtension: z
    .number()
    .min(SCAN_SETTING_LIMITS.maxAtrExtension.min)
    .max(SCAN_SETTING_LIMITS.maxAtrExtension.max),
  excludeBiotech: z.boolean(),
});
export type ScanSettings = z.infer<typeof ScanSettingsSchema>;

export const DEFAULT_SCAN_SETTINGS: ScanSettings = {
  minAverageDollarVolume30d: 30_000_000,
  minAdrPct: 4,
  minAverageVolume10d: 350_000,
  topPct: 0.05,
  maxAtrExtension: 4,
  excludeBiotech: true,
};

/** The loosest allowed settings; scans check coils for every leader they would produce. */
export const LOOSEST_SCAN_SETTINGS: ScanSettings = {
  minAverageDollarVolume30d: SCAN_SETTING_LIMITS.minAverageDollarVolume30d.min,
  minAdrPct: SCAN_SETTING_LIMITS.minAdrPct.min,
  minAverageVolume10d: SCAN_SETTING_LIMITS.minAverageVolume10d.min,
  topPct: SCAN_SETTING_LIMITS.topPct.max,
  maxAtrExtension: SCAN_SETTING_LIMITS.maxAtrExtension.max,
  excludeBiotech: false,
};

/** One liquid stock in a session's stored universe; `coil` is null when it was not checked. */
export const UniverseStockSchema = ScanCandidateSchema.omit({ topWindows: true, pairedEtf: true });
export type UniverseStock = z.infer<typeof UniverseStockSchema>;

export const StockUniverseSnapshotSchema = z.object({
  sessionDate: z.string(),
  generatedAt: z.string(),
  minAverageDollarVolume30d: z.number(),
  stocks: z.array(UniverseStockSchema),
});
export type StockUniverseSnapshot = z.infer<typeof StockUniverseSnapshotSchema>;

export interface ScanCandidateDto extends ScanCandidate {
  watched: boolean;
}

export interface MarketStocksDto {
  status: 'READY' | 'NOT_AVAILABLE';
  snapshot:
    | (Omit<StockLeadersSnapshot, 'nonExtendedLeaders' | 'superLiquid'> & {
        nonExtendedLeaders: ScanCandidateDto[];
        superLiquid: ScanCandidateDto[];
        /** Every momentum leader, extended or not; null when the session has no stored universe. */
        momentumLeaders: ScanCandidateDto[] | null;
        /** The user saved their own thresholds. */
        customSettings: boolean;
        /** False when custom thresholds could not be applied because the universe was not kept. */
        settingsApplied: boolean;
      })
    | null;
  source: { name: string; url: string };
  informationalOnly: true;
}

export interface ScanSettingsDto {
  settings: ScanSettings;
  isDefault: boolean;
  defaults: ScanSettings;
}

/** Every stock passing the scan filters, ranked or not. */
export interface MarketUniverseDto {
  status: 'READY' | 'NOT_AVAILABLE';
  sessionDate: string | null;
  stocks: ScanCandidateDto[];
}
